import { useState } from 'react';
import { vaultDocTypes, type UpdateVaultDocumentInput, type VaultDocument } from '@accounting/shared';
import { formatCurrency, humanizeLabel } from '../../lib/formatters';
import { openFileInNewTab } from '../../lib/api';
import { formatBytes } from './VaultUploadPanel';

/**
 * True when the AI could not confidently file the document: no date, an outright
 * failure, or a low-confidence guess. These are the rows worth the owner's time.
 */
export function needsAttention(doc: VaultDocument): boolean {
  if (doc.reviewedByUser) return false;
  if (doc.extractionStatus === 'failed') return true;
  if (!doc.documentDate) return true;
  return doc.extractionConfidence !== null && Number(doc.extractionConfidence) <= 0.5;
}

export function VaultDocumentRow({
  companyId,
  doc,
  onSave,
  onDelete,
  onRetry,
  saving,
}: {
  companyId: string;
  doc: VaultDocument;
  onSave: (id: string, input: UpdateVaultDocumentInput) => void;
  onDelete: (doc: VaultDocument) => void;
  onRetry: (id: string) => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const attention = needsAttention(doc);

  const view = () =>
    void openFileInNewTab(`/companies/${companyId}/vault/documents/${doc.id}/download`);

  return (
    <>
      <tr>
        <td style={{ whiteSpace: 'nowrap' }}>
          {doc.documentDate ?? (
            <span className="muted" title={`Filed under its upload date, ${doc.createdAt.slice(0, 10)}`}>
              {doc.createdAt.slice(0, 10)} ?
            </span>
          )}
        </td>
        <td>
          <button
            className="amount-link"
            onClick={view}
            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}
          >
            {doc.fileName}
          </button>
          {doc.summary && (
            <div className="muted" style={{ fontSize: 11 }}>
              {doc.summary}
            </div>
          )}
          {doc.extractionStatus === 'failed' && doc.extractionError && (
            <div className="muted" style={{ fontSize: 11 }}>
              ⚠ {doc.extractionError}
            </div>
          )}
        </td>
        <td>{doc.docType ? <span className="tag info">{humanizeLabel(doc.docType)}</span> : ''}</td>
        <td>{doc.vendorName ?? ''}</td>
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          {doc.amount ? formatCurrency(doc.amount) : ''}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {doc.extractionStatus === 'pending' || doc.extractionStatus === 'processing' ? (
            <span className="tag">Reading…</span>
          ) : attention ? (
            <span className="tag warn">Needs review</span>
          ) : doc.reviewedByUser ? (
            <span className="tag ok">Reviewed</span>
          ) : (
            ''
          )}
        </td>
        <td className="muted" style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: 11 }}>
          {formatBytes(doc.sizeBytes)}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button className="btn-secondary" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Close' : 'Edit'}
          </button>
        </td>
      </tr>

      {editing && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--bg-surface-hover)' }}>
            <VaultDocumentEditor
              doc={doc}
              saving={saving}
              onCancel={() => setEditing(false)}
              onSave={(input) => {
                onSave(doc.id, input);
                setEditing(false);
              }}
              onRetry={() => onRetry(doc.id)}
              onDelete={() => onDelete(doc)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function VaultDocumentEditor({
  doc,
  saving,
  onSave,
  onCancel,
  onRetry,
  onDelete,
}: {
  doc: VaultDocument;
  saving: boolean;
  onSave: (input: UpdateVaultDocumentInput) => void;
  onCancel: () => void;
  onRetry: () => void;
  onDelete: () => void;
}) {
  const [documentDate, setDocumentDate] = useState(doc.documentDate ?? '');
  const [docType, setDocType] = useState(doc.docType ?? '');
  const [vendorName, setVendorName] = useState(doc.vendorName ?? '');
  const [amount, setAmount] = useState(doc.amount ? Number(doc.amount).toFixed(2) : '');
  const [summary, setSummary] = useState(doc.summary ?? '');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (amount.trim() && !/^-?\d+(\.\d{1,4})?$/.test(amount.trim())) {
      setError('Amount must be a plain number, e.g. 1240.55');
      return;
    }
    onSave({
      documentDate: documentDate || null,
      docType: (docType || null) as UpdateVaultDocumentInput['docType'],
      vendorName: vendorName.trim() || null,
      amount: amount.trim() || null,
      summary: summary.trim() || null,
      reviewedByUser: true,
    });
  };

  return (
    <div style={{ padding: '10px 2px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ fontSize: 12 }}>
          Document date
          <br />
          <input type="date" value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
        </label>
        <label style={{ fontSize: 12 }}>
          Type
          <br />
          <select value={docType} onChange={(e) => setDocType(e.target.value)}>
            <option value="">—</option>
            {vaultDocTypes.map((t) => (
              <option key={t} value={t}>
                {humanizeLabel(t)}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          Vendor
          <br />
          <input value={vendorName} onChange={(e) => setVendorName(e.target.value)} placeholder="Acme Supply" />
        </label>
        <label style={{ fontSize: 12 }}>
          Amount
          <br />
          <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1240.55" style={{ width: 110 }} />
        </label>
        <label style={{ fontSize: 12, flex: 1, minWidth: 240 }}>
          Summary
          <br />
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="One line — what is this?"
            style={{ width: '100%' }}
          />
        </label>
      </div>

      {error && <div className="field-error">{error}</div>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn-primary" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : 'Save & mark reviewed'}
        </button>
        <button className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn-secondary" onClick={onRetry} disabled={saving} title="Ask the AI to read this file again">
          Re-read with AI
        </button>
        <span style={{ flex: 1 }} />
        <button className="btn-danger" onClick={onDelete}>
          Delete
        </button>
      </div>

      <div className="muted" style={{ fontSize: 11 }}>
        Uploaded {new Date(doc.createdAt).toLocaleString()} · {doc.mimeType} · SHA-256 {doc.sha256.slice(0, 12)}…
        {doc.extractionConfidence !== null && ` · AI confidence ${Math.round(Number(doc.extractionConfidence) * 100)}%`}
      </div>
    </div>
  );
}
